/**
 * svgTransfo — outillage SVG partagé du dossier transformations (5e, thème #12).
 *
 * Grille / repère orthogonal + figures et leurs symétriques par demi-tour,
 * tout PAR CONSTRUCTION (la figure image est réellement le symétrique central
 * D = 2C − A des sommets). Repris de gen_945 (svgRepere : graduations balisées
 * tx/ty, points pt/pl) + tracé de polygones, segments, centre, croix lettrées,
 * codages de longueur, grille de nœuds lettrés (Ex.3).
 *
 * Toutes les coordonnées d'ENTRÉE sont des entiers (ou demi-entiers) de grille ;
 * le rendu mappe vers les pixels. Couleur source = bleu primaire TanoEd,
 * image = orange, centre = rouge.
 */

import { subscriptToSvgTspan } from '../../../utils';

export const BLEU = '#1E3A8A';
export const ORANGE = '#b45309';
export const ROUGE = '#dc2626';
export const VERT = '#15803d';

/** Symétrique central de [x,y] par rapport au centre [cx,cy] : 2C − P. */
export const symCentral = ([x, y], [cx, cy]) => [2 * cx - x, 2 * cy - y];

/** Écriture française d'un entier ou demi-entier ("-1,5", "2"). */
export const fr = (v) => {
    const s = v < 0 ? '-' : '';
    const a = Math.abs(v);
    return s + (Number.isInteger(a) ? String(a) : String(a).replace('.', ','));
};
export const paire = (x, y) => `(${fr(x)} ; ${fr(y)})`;

/**
 * Grille orthogonale. Options :
 *   xMin,xMax,yMin,yMax : bornes entières de la grille.
 *   axes    : true → repère avec axes fléchés + graduations étiquetées (tx/ty).
 *   points  : [{x,y,label,couleur?}]   nœuds nommés (cercle + étiquette).
 *   centres : [{x,y,label,couleur?}]   centres de symétrie (croix + étiquette).
 *   croix   : [{x,y,label}]            croix candidates lettrées (minuscule).
 *   polys   : [{pts:[[x,y]…], couleur, dash?, label?}]  polygones fermés.
 *   segments: [{a:[x,y], b:[x,y], couleur, dash?}]
 *   fleches : [{a:[x,y], b:[x,y], couleur?, label?}]  vecteur a→b (pointe + étiquette).
 *   codes   : [{a:[x,y], b:[x,y], marques?}]  codage de longueur (tirets ⁄ ⁄⁄).
 *   lettresNoeuds : true → dessine TOUS les nœuds de la grille en petits points.
 *   desc    : string → balise <desc> auto-descriptive (lue par le solveur, jamais rendue).
 */
export const svgGrille = (cfg) => {
    const { xMin, xMax, yMin, yMax, axes = false,
        points = [], centres = [], croix = [], polys = [], segments = [], fleches = [], codes = [],
        lettresNoeuds = false, desc = null } = cfg;
    const PAD = 30;
    const span = Math.max(xMax - xMin, yMax - yMin);
    const STEP = Math.min(46, Math.floor(360 / span));
    const W = PAD * 2 + (xMax - xMin) * STEP;
    const H = PAD * 2 + (yMax - yMin) * STEP;
    const px = (x) => Math.round((PAD + (x - xMin) * STEP) * 10) / 10;
    const py = (y) => Math.round((PAD + (yMax - y) * STEP) * 10) / 10;
    const out = [];
    out.push(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" width="${W}" height="${H}" font-family="system-ui, sans-serif">`);
    if (desc) out.push(`<desc>${desc}</desc>`);
    out.push(`<rect x="0" y="0" width="${W}" height="${H}" fill="white"/>`);

    // Quadrillage
    for (let x = xMin; x <= xMax; x++) {
        out.push(`<line x1="${px(x)}" y1="${py(yMax)}" x2="${px(x)}" y2="${py(yMin)}" stroke="#e2e8f0" stroke-width="1"/>`);
    }
    for (let y = yMin; y <= yMax; y++) {
        out.push(`<line x1="${px(xMin)}" y1="${py(y)}" x2="${px(xMax)}" y2="${py(y)}" stroke="#e2e8f0" stroke-width="1"/>`);
    }

    if (axes) {
        const x0 = px(0), y0 = py(0);
        out.push(`<line x1="${px(xMin)}" y1="${y0}" x2="${px(xMax) + 8}" y2="${y0}" stroke="#334155" stroke-width="1.6"/>`);
        out.push(`<polygon points="${px(xMax) + 12},${y0} ${px(xMax) + 4},${y0 - 4} ${px(xMax) + 4},${y0 + 4}" fill="#334155"/>`);
        out.push(`<line x1="${x0}" y1="${py(yMin)}" x2="${x0}" y2="${py(yMax) - 8}" stroke="#334155" stroke-width="1.6"/>`);
        out.push(`<polygon points="${x0},${py(yMax) - 12} ${x0 - 4},${py(yMax) - 4} ${x0 + 4},${py(yMax) - 4}" fill="#334155"/>`);
        for (let x = xMin; x <= xMax; x++) {
            if (x === 0) continue;
            out.push(`<text class="tx" x="${px(x)}" y="${y0 + 15}" text-anchor="middle" font-size="11" fill="#1e293b">${fr(x)}</text>`);
        }
        for (let y = yMin; y <= yMax; y++) {
            if (y === 0) continue;
            out.push(`<text class="ty" x="${x0 - 6}" y="${py(y)}" text-anchor="end" dominant-baseline="middle" font-size="11" fill="#1e293b">${fr(y)}</text>`);
        }
        out.push(`<text x="${x0 - 6}" y="${y0 + 15}" text-anchor="end" font-size="11" fill="#1e293b">0</text>`);
    }

    if (lettresNoeuds) {
        for (let y = yMax; y >= yMin; y--) {
            for (let x = xMin; x <= xMax; x++) {
                out.push(`<circle cx="${px(x)}" cy="${py(y)}" r="1.7" fill="#94a3b8"/>`);
            }
        }
    }

    // Polygones (fermés)
    polys.forEach((pl) => {
        const pts = pl.pts.map(([x, y]) => `${px(x)},${py(y)}`).join(' ');
        out.push(`<polygon class="poly" points="${pts}" fill="${pl.couleur}" fill-opacity="0.10" stroke="${pl.couleur}" stroke-width="2"${pl.dash ? ' stroke-dasharray="6 4"' : ''}/>`);
        if (pl.label) {
            const cx = pl.pts.reduce((s, p) => s + p[0], 0) / pl.pts.length;
            const cy = pl.pts.reduce((s, p) => s + p[1], 0) / pl.pts.length;
            out.push(`<text x="${px(cx)}" y="${py(cy) + 4}" text-anchor="middle" font-size="14" font-weight="700" fill="${pl.couleur}">${pl.label}</text>`);
        }
    });

    // Segments
    segments.forEach((sg) => {
        out.push(`<line class="sg" x1="${px(sg.a[0])}" y1="${py(sg.a[1])}" x2="${px(sg.b[0])}" y2="${py(sg.b[1])}" stroke="${sg.couleur}" stroke-width="2.2"${sg.dash ? ' stroke-dasharray="6 4"' : ''}/>`);
    });

    // Flèches de vecteur (translation) : trait + pointe à l'extrémité b + étiquette.
    if (fleches.length) {
        fleches.forEach((f, i) => {
            const col = f.couleur || VERT;
            const id = `arw${i}`;
            const ax = px(f.a[0]), ay = py(f.a[1]), bx = px(f.b[0]), by = py(f.b[1]);
            out.push(`<defs><marker id="${id}" markerWidth="9" markerHeight="9" refX="7" refY="3" orient="auto" markerUnits="userSpaceOnUse"><path d="M0,0 L7,3 L0,6 Z" fill="${col}"/></marker></defs>`);
            out.push(`<line x1="${ax}" y1="${ay}" x2="${bx}" y2="${by}" stroke="${col}" stroke-width="2.4" marker-end="url(#${id})"/>`);
            if (f.label) {
                const mx = (ax + bx) / 2, my = (ay + by) / 2;
                const len = Math.hypot(bx - ax, by - ay) || 1;
                const nx = -(by - ay) / len, ny = (bx - ax) / len;   // perpendiculaire
                out.push(`<text x="${(mx + nx * 12).toFixed(1)}" y="${(my + ny * 12).toFixed(1)}" text-anchor="middle" dominant-baseline="middle" font-size="14" font-weight="700" fill="${col}" paint-order="stroke" stroke="white" stroke-width="3">${f.label}</text>`);
            }
        });
    }

    // Codages de longueur : n tirets perpendiculaires au segment, à son milieu.
    codes.forEach((c) => {
        const ax = px(c.a[0]), ay = py(c.a[1]), bx = px(c.b[0]), by = py(c.b[1]);
        const mx = (ax + bx) / 2, my = (ay + by) / 2;
        const len = Math.hypot(bx - ax, by - ay) || 1;
        const ux = (bx - ax) / len, uy = (by - ay) / len;   // le long du segment
        const nx = -uy, ny = ux;                             // perpendiculaire
        const n = c.marques || 1;
        for (let i = 0; i < n; i++) {
            const t = (i - (n - 1) / 2) * 5;
            const cxp = mx + ux * t, cyp = my + uy * t;
            out.push(`<line x1="${(cxp + nx * 5).toFixed(1)}" y1="${(cyp + ny * 5).toFixed(1)}" x2="${(cxp - nx * 5).toFixed(1)}" y2="${(cyp - ny * 5).toFixed(1)}" stroke="#0f172a" stroke-width="1.6"/>`);
        }
    });

    // Croix candidates (lettrées, minuscules)
    croix.forEach((k) => {
        const cx = px(k.x), cy = py(k.y);
        out.push(`<g class="cx"><line x1="${cx - 5}" y1="${cy - 5}" x2="${cx + 5}" y2="${cy + 5}" stroke="${ROUGE}" stroke-width="2"/><line x1="${cx - 5}" y1="${cy + 5}" x2="${cx + 5}" y2="${cy - 5}" stroke="${ROUGE}" stroke-width="2"/><text class="cl" x="${cx + 8}" y="${cy - 7}" font-size="14" font-weight="700" fill="${ROUGE}">${k.label}</text></g>`);
    });

    // Position d'étiquette (revue 2026-08-13) : à droite du point, centrée
    // verticalement — sauf point SUR l'axe horizontal (y = 0), où l'étiquette
    // passe au-dessus pour ne pas être posée sur le trait de l'axe.
    const attrsEtiquette = (y, cx, cy) => (axes && y === 0
        ? `x="${cx}" y="${cy - 9}" text-anchor="middle"`
        : `x="${cx + 8}" y="${cy + 5}"`);

    // Centres de symétrie (croix + étiquette)
    centres.forEach((c) => {
        const cx = px(c.x), cy = py(c.y);
        const col = c.couleur || ROUGE;
        out.push(`<g class="ce"><line x1="${cx - 5}" y1="${cy}" x2="${cx + 5}" y2="${cy}" stroke="${col}" stroke-width="2"/><line x1="${cx}" y1="${cy - 5}" x2="${cx}" y2="${cy + 5}" stroke="${col}" stroke-width="2"/><text ${attrsEtiquette(c.y, cx, cy)} font-size="14" font-weight="700" fill="${col}">${c.label || ''}</text></g>`);
    });

    // Points nommés
    points.forEach((p) => {
        const cx = px(p.x), cy = py(p.y);
        const col = p.couleur || BLEU;
        out.push(`<g class="pt"><circle cx="${cx}" cy="${cy}" r="4" fill="${col}"/><text class="pl" ${attrsEtiquette(p.y, cx, cy)} font-size="14" font-weight="700" fill="${col}">${p.label || ''}</text></g>`);
    });

    out.push('</svg>');
    return out.join('');
};

/** Élément additionnel d'énoncé (imageData en data-URI ; lu tel quel par le solveur). */
export const elementGrille = (cfg) => ({
    version: 1,
    elements: [{ type: 'image', config: { imageData: 'data:image/svg+xml,' + encodeURIComponent(svgGrille(cfg)), alt: 'Figure' } }],
});
